"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { IconChevronRight, IconClipboardList } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { useTreatmentName } from "@/hooks/use-treatment-name"
import { listMyTreatments } from "@/lib/treatments-api"
import { cn } from "@/lib/utils"
import type { ClientTreatment, ClientTreatmentStage } from "@/types"

/** `yyyy-MM-dd` → local midnight. `new Date("2026-07-30")` would parse as UTC. */
function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfToday(): Date {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return now
}

const DAY_MS = 86_400_000

function nextPendingStage(treatment: ClientTreatment): ClientTreatmentStage | null {
  return (
    (treatment.stages ?? [])
      .filter((s) => s.enabled && s.status === "pending")
      .sort((a, b) => a.order - b.order)[0] ?? null
  )
}

/**
 * Phase 89 D-7 — the doctor's worklist: every planned or active treatment
 * assigned to them, across patients, soonest pending step first (the server
 * orders it). Self-fetching: the parent only decides whether it is shown
 * (treatment_assignment_enabled + kartele.view).
 *
 * Each row links to the patient's kartele, where the step is marked done.
 */
export function MyTreatmentsPanel({ className }: { className?: string }) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const names = useTreatmentName()
  const [treatments, setTreatments] = useState<ClientTreatment[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    listMyTreatments()
      .then((rows) => {
        if (!cancelled) setTreatments(rows)
      })
      .catch(() => {
        if (!cancelled) setTreatments([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("myTreatments.title")} className={className}>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-24 shrink-0" />
              <Skeleton className="h-4 w-16 shrink-0" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (treatments.length === 0) {
    return (
      <ApolloPanel eyebrow={t("myTreatments.title")} className={className}>
        <div className="flex h-[180px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconClipboardList className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("myTreatments.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("myTreatments.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  const today = startOfToday()

  return (
    <ApolloPanel
      eyebrow={t("myTreatments.title")}
      actions={
        <Badge variant="outline" className="text-[10px]">
          {t("myTreatments.count", { count: treatments.length })}
        </Badge>
      }
      className={className}
    >
      <div className="space-y-1">
        {treatments.map((treatment) => {
          const stage = nextPendingStage(treatment)
          const planned = stage?.planned_date ? parseLocalDate(stage.planned_date) : null
          const diffDays = planned
            ? Math.round((planned.getTime() - today.getTime()) / DAY_MS)
            : null
          const isOverdue = diffDays !== null && diffDays < 0

          let whenLabel: string
          if (!planned || diffDays === null) whenLabel = t("myTreatments.unscheduled")
          else if (diffDays === 0) whenLabel = t("myTreatments.today")
          else whenLabel = formatter.relativeTime(planned, today)

          const href = treatment.client
            ? `/leads/${treatment.client.lead_id}?tab=kartele`
            : "#"

          return (
            <Link
              key={treatment.id}
              href={href}
              className="group flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/60"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {treatment.client?.name ?? "—"}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {names.treatment(treatment)}
                  {stage ? ` · ${names.stage(stage)}` : ""}
                  {treatment.teeth_json.length > 0 && (
                    <span className="font-mono tabular-nums">
                      {" · "}
                      {treatment.teeth_json.join("/")}
                    </span>
                  )}
                </span>
              </span>
              <span
                className={cn(
                  "shrink-0 text-xs tabular-nums",
                  isOverdue ? "font-medium text-destructive" : "text-muted-foreground",
                )}
              >
                {whenLabel}
              </span>
              <IconChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground" />
            </Link>
          )
        })}
      </div>
    </ApolloPanel>
  )
}
